import { useEffect, useRef, useState } from 'react'

// One diagram, on one unlisted page (graphql/itineraries/itineraries-model.md).
//
// Mermaid is ~2.5MB, so it is imported dynamically: Vite splits it into its own
// chunk and the other 42 pages never fetch it. Keeping the diagram as a
// ```mermaid fence rather than a committed SVG means the source stays editable
// in the markdown, which is the whole reason the diagram is legible in a diff.

let counter = 0
const nextId = (): string => `curri-mermaid-${(counter += 1)}`

export const Mermaid = ({ chart }: { chart: string }) => {
  const container = useRef<HTMLDivElement>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false

    void import('mermaid')
      .then(async ({ default: mermaid }) => {
        mermaid.initialize({ startOnLoad: false, theme: 'neutral' })
        const { svg } = await mermaid.render(nextId(), chart)
        if (cancelled || !container.current) return
        container.current.innerHTML = svg
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
    }
  }, [chart])

  // A diagram that will not render falls back to its source rather than to an
  // empty box — the fence is readable text, and a blank space says nothing.
  if (failed) return <pre className="curri-code__pre">{chart}</pre>

  return <div className="curri-mermaid" ref={container} />
}
